/**
 * AdminPage — 管理者画面 (/admin)
 *
 * - 管理者パスワードで入る（このタブの中だけ覚える。タブを閉じると再入力）
 * - 売上の一覧・合計、CSVのダウンロード
 * - 今日の接続テストの回数の確認とリセット
 * - 管理者用の接続テスト（8時間）を始める
 * お客様向けの画面からはリンクしない。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787';
const KEY = 'amo_admin_secret';

type SaleRow = {
  created_at: number;
  purchase_kind: string | null;
  currency: string | null;
  amount_subtotal: number | null;
  discount_amount: number | null;
  amount_tax: number | null;
  amount_total: number | null;
  refunded_amount: number | null;
  customer_email: string | null;
  customer_country: string | null;
  company_name: string | null;
  discount_code: string | null;
  purchased_hours: number | null;
  payment_status: string | null;
  started_at: number | null;
  event_name: string | null;
};

type LimitRow = { eventId: string; eventName: string | null; count: number; updatedAt: number };

function getSecret(): string {
  try { return sessionStorage.getItem(KEY) ?? ''; } catch { return ''; }
}

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function money(v: number | null, currency: string | null): string {
  if (v === null || v === undefined) return '';
  if (currency === 'usd') return `$${(v / 100).toFixed(2)}`;
  return `¥${Number(v).toLocaleString('ja-JP')}`;
}

const KIND: Record<string, string> = { initial: '初回', extension: '延長' };
const STATUS: Record<string, string> = { paid: '支払い済み', pending: '未完了', refunded: '返金', expired: '期限切れ' };

export default function AdminPage() {
  const [secret, setSecret] = useState(getSecret());
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const now = new Date();
  const [from, setFrom] = useState(ymd(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(ymd(now));
  const [includeAll, setIncludeAll] = useState(false);
  const [rows, setRows] = useState<SaleRow[] | null>(null);
  const [limits, setLimits] = useState<LimitRow[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 検索エンジンに載せない
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    document.title = 'Amo 管理者画面';
    return () => { document.head.removeChild(m); };
  }, []);

  const call = useCallback(async (path: string, init: RequestInit = {}, s = secret) => {
    const res = await fetch(`${WORKER_URL}${path}`, {
      ...init,
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${s}` },
    });
    if (res.status === 401 || res.status === 429) {
      const d = (await res.json().catch(() => ({}))) as { message?: string };
      try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
      setAuthed(false);
      setSecret('');
      throw new Error(d.message ?? 'パスワードを確認できませんでした。');
    }
    if (!res.ok) throw new Error(`エラーが発生しました（${res.status}）`);
    return res;
  }, [secret]);

  // 期間（終了日はその日の終わりまで含める）
  const range = useMemo(() => {
    const end = new Date(`${to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    const q = new URLSearchParams({ from: new Date(`${from}T00:00:00`).toISOString(), to: end.toISOString() });
    if (includeAll) q.set('status', 'all');
    return q.toString();
  }, [from, to, includeAll]);

  const loadSales = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const d = (await (await call(`/admin/sales?${range}`)).json()) as { rows: SaleRow[] };
      setRows(d.rows);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }, [call, range]);

  const loadLimits = useCallback(async () => {
    try {
      const d = (await (await call('/admin/test-limits')).json()) as { limits: LimitRow[] };
      setLimits(d.limits);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [call]);

  // 保存済みのパスワードがあれば、そのまま入る
  useEffect(() => {
    if (!secret || authed) return;
    call('/admin/check', {}, secret).then(() => setAuthed(true)).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authed) return;
    void loadSales();
    void loadLimits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async () => {
    const s = pw.trim();
    if (!s) return;
    setBusy(true); setError(null);
    try {
      await call('/admin/check', {}, s);
      try { sessionStorage.setItem(KEY, s); } catch { /* ignore */ }
      setSecret(s);
      setPw('');
      setAuthed(true);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const logout = () => {
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
    setSecret(''); setAuthed(false); setRows(null); setLimits(null);
  };

  const downloadCsv = async () => {
    setBusy(true); setError(null);
    try {
      const blob = await (await call(`/admin/sales.csv?${range}`)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `amo-sales_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const resetLimit = async (eventId: string | null, label: string) => {
    if (!window.confirm(`${label}の今日の接続テストの回数を0に戻します。よろしいですか？`)) return;
    setError(null);
    try {
      await call('/admin/reset-test-limit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventId ? { eventId } : { all: true }),
      });
      setNotice(`${label}の回数を0に戻しました。`);
      await loadLimits();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  // 合計（通貨ごと。支払い済みから返金を引いた額）
  const totals = useMemo(() => {
    const t: Record<string, { count: number; total: number; refunded: number }> = {};
    for (const r of rows ?? []) {
      if (r.payment_status !== 'paid' && r.payment_status !== 'refunded') continue;
      const c = r.currency ?? 'jpy';
      t[c] ??= { count: 0, total: 0, refunded: 0 };
      t[c].count += 1;
      t[c].total += r.amount_total ?? 0;
      t[c].refunded += r.refunded_amount ?? 0;
    }
    return t;
  }, [rows]);

  const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem', marginBottom: '1.25rem' };
  const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.5rem', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontWeight: 600, color: '#6b7280' };
  const td: React.CSSProperties = { padding: '0.4rem 0.5rem', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ ...box, width: '100%', maxWidth: 380 }}>
          <h1 style={{ fontSize: '1.2rem', margin: '0 0 1rem' }}>Amo 管理者画面</h1>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="管理者パスワード"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void login(); }}
            style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem', boxSizing: 'border-box' }}
          />
          <button className="btn btn-primary" style={{ width: '100%', marginTop: '0.75rem' }} onClick={() => void login()} disabled={busy}>
            {busy ? '確認中…' : '入る'}
          </button>
          {error && <div className="alert alert-error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.75rem' }}>パスワードはこのタブの中だけで覚えます。共用のパソコンでは使わないでください。</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '1.25rem', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#111827' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h1 style={{ fontSize: '1.3rem', margin: 0 }}>Amo 管理者画面</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <a className="btn btn-ghost btn-sm" href="/setup?admin=1">管理者用の接続テスト（8時間）</a>
            <button className="btn btn-ghost btn-sm" onClick={logout}>ログアウト</button>
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
        {notice && <div className="alert alert-info" style={{ marginBottom: '1rem' }}>{notice}</div>}

        {/* 売上 */}
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>売上</div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <label>開始 <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label>終了 <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <label><input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} /> 未完了・期限切れも表示</label>
            <button className="btn btn-primary btn-sm" onClick={() => void loadSales()} disabled={busy}>表示</button>
            <button className="btn btn-ghost btn-sm" onClick={() => void downloadCsv()} disabled={busy}>CSVをダウンロード</button>
          </div>

          {Object.keys(totals).length > 0 && (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {Object.entries(totals).map(([c, v]) => (
                <div key={c}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{c.toUpperCase()}・{v.count}件</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{money(v.total - v.refunded, c)}</div>
                  {v.refunded > 0 && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>（返金 {money(v.refunded, c)} を差し引き）</div>}
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', width: '100%' }}>
              <thead>
                <tr>
                  {['購入日時', 'イベント', '区分', '時間', '金額', '割引', '返金', 'メール', '国', '会社名', '割引コード', '状態', '利用開始'].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{new Date(r.created_at * 1000).toLocaleString('ja-JP')}</td>
                    <td style={td}>{r.event_name ?? ''}</td>
                    <td style={td}>{KIND[r.purchase_kind ?? ''] ?? r.purchase_kind ?? ''}</td>
                    <td style={td}>{r.purchased_hours ?? ''}h</td>
                    <td style={td}>{money(r.amount_total, r.currency)}</td>
                    <td style={td}>{r.discount_amount ? money(r.discount_amount, r.currency) : ''}</td>
                    <td style={td}>{r.refunded_amount ? money(r.refunded_amount, r.currency) : ''}</td>
                    <td style={td}>{r.customer_email ?? ''}</td>
                    <td style={td}>{r.customer_country ?? ''}</td>
                    <td style={td}>{r.company_name ?? ''}</td>
                    <td style={td}>{r.discount_code ?? ''}</td>
                    <td style={td}>{STATUS[r.payment_status ?? ''] ?? r.payment_status ?? ''}</td>
                    <td style={td}>{r.started_at ? new Date(r.started_at * 1000).toLocaleString('ja-JP') : '未開始'}</td>
                  </tr>
                ))}
                {rows && rows.length === 0 && (
                  <tr><td style={td} colSpan={13}>この期間の売上はありません。</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 接続テストの回数 */}
        <div style={box}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ fontWeight: 700 }}>今日の接続テストの回数（1日3回まで）</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => void loadLimits()}>更新</button>
              <button className="btn btn-ghost btn-sm" onClick={() => void resetLimit(null, 'すべてのイベント')}>すべて0に戻す</button>
            </div>
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', width: '100%' }}>
            <thead>
              <tr>{['イベント', '回数', '最後に使った時刻', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {(limits ?? []).map((l) => (
                <tr key={l.eventId}>
                  <td style={td}>{l.eventName ?? l.eventId}</td>
                  <td style={td}>{l.count} / 3</td>
                  <td style={td}>{new Date(l.updatedAt * 1000).toLocaleString('ja-JP')}</td>
                  <td style={td}>
                    <button className="btn btn-ghost btn-sm" onClick={() => void resetLimit(l.eventId, `「${l.eventName ?? l.eventId}」`)}>0に戻す</button>
                  </td>
                </tr>
              ))}
              {limits && limits.length === 0 && (
                <tr><td style={td} colSpan={4}>今日はまだ接続テストが使われていません。</td></tr>
              )}
            </tbody>
          </table>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
            0に戻すと、同じ回線からの回数制限（1日3回）もあわせて0に戻ります。
          </p>
        </div>
      </div>
    </div>
  );
}
